// ---------------------------------------------------------------------------
// Numbers selection: one player calls how many of the six tiles are large.
//
// The call itself is one tap, and the server answers it by drawing the board and
// moving everyone to numbers-play in the same beat. So the interesting half of
// this screen is the other players': they are about to get thirty seconds and
// they must not spend the wait wondering whether anything is happening.
//
// There is deliberately no client-side default. When the selection clock expires
// the server draws the board itself; a fallback here could draw a different one,
// and two clients disagreeing about the tiles is worse than a short wait.
//
// **A watcher gets the chooser's name and nothing else**, the same as the letters
// selection screen. What was here was a panel of large-number chips, a sentence
// naming those same chips, and a promise about when their own clock would start
// — three things to read on a screen whose entire job is a two-second wait, none
// of which a watcher can act on. The tiles themselves answer all of it a beat
// later.
//
// The one line the chooser still gets under the buttons sits in a reserved slot,
// so it cannot grow the card at the moment of the tap.
// ---------------------------------------------------------------------------

import { useState } from 'react';
import { NUMBERS_LARGE_TILES, NUMBERS_MAX_LARGE, NUMBERS_MIN_LARGE } from '@countdown/shared';
import { LiveClock } from '../../components/primitives/LiveClock.js';
import { PlayerChip } from '../../components/primitives/PlayerChip.js';
import { StandingsStrip } from '../../components/primitives/StandingsStrip.js';
import { useClock } from '../../hooks/useClock.js';
import { useIsDesktop } from '../../hooks/useMediaQuery.js';
import { roundLabel } from '../../lib/format.js';
import { playerById } from '../../lib/playerLookup.js';
import type { RoomScreenProps } from './types.js';

const MONO_LABEL = 'font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-ink-dim';

const LARGE_COUNTS: readonly number[] = Array.from(
  { length: NUMBERS_MAX_LARGE - NUMBERS_MIN_LARGE + 1 },
  (_, index) => NUMBERS_MIN_LARGE + index,
);

/** Spoken form of a count, so the five buttons are not five bare digits. */
function largeCountLabel(count: number): string {
  if (count === 0) return 'no large numbers';
  if (count === 1) return '1 large number';
  return `${count} large numbers`;
}

export function NumbersSelectionScreen({ state, send }: RoomScreenProps) {
  const round = state.rounds[state.currentRoundIndex] ?? null;
  const chooser = playerById(state, round?.chooserId ?? null);
  const youAreChoosing = round?.chooserId === state.youId;
  const isDesktop = useIsDesktop();
  const clock = useClock(round?.clock ?? null, { frozen: state.serverPaused });

  // Local only, to stop a second tap racing the phase change. The board itself
  // always comes from the server.
  const [called, setCalled] = useState<number | null>(null);
  const chosen = round?.largeCount ?? called;

  const clockVariant = clock.frozen ? 'frozen' : clock.urgent ? 'alert' : 'normal';

  const call = (largeCount: number) => {
    if (chosen !== null) return;
    setCalled(largeCount);
    send({ type: 'choose-large-count', largeCount, actionId: crypto.randomUUID() });
  };

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-4 p-4 md:gap-6 md:p-6">
      <StandingsStrip state={state} showRoundCounter />

      <header className="flex items-center gap-3">
        <span className="rounded-[5px] bg-raised px-2.5 py-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-ink">
          {`${roundLabel(round?.type ?? 'numbers').toLowerCase()} ${(round?.index ?? state.currentRoundIndex) + 1} of ${state.totalRounds}`}
        </span>
        <div className="ml-auto">
          <LiveClock
            clock={round?.clock ?? null}
            frozen={state.serverPaused}
            size={isDesktop ? 128 : 96}
            variant={clockVariant}
            label={`${clock.seconds} seconds left to call the board`}
          />
        </div>
      </header>

      {youAreChoosing ? (
        <section className="flex flex-col gap-5 rounded-frame border border-line bg-surface p-4 md:p-6">
          <div className="flex flex-col gap-1.5">
            <span className={MONO_LABEL}>you are choosing</span>
            <h2 className="text-2xl font-bold tracking-tight">Pick how many large numbers.</h2>
          </div>

          <div className="flex flex-wrap gap-2">
            {LARGE_COUNTS.map((count) => {
              const picked = chosen === count;
              return (
                <button
                  key={count}
                  type="button"
                  aria-label={largeCountLabel(count)}
                  aria-pressed={picked}
                  disabled={chosen !== null && !picked}
                  onClick={() => call(count)}
                  className={[
                    'grid h-[52px] w-[52px] place-items-center rounded-tile font-mono text-xl font-bold',
                    'transition-[background-color,border-color,opacity] duration-150 ease-out',
                    picked
                      ? 'btn-primary-surface'
                      : 'border border-line-strong bg-raised text-ink disabled:opacity-40',
                  ].join(' ')}
                >
                  {count}
                </button>
              );
            })}
          </div>

          <div className="flex flex-col gap-1.5">
            {/* The one thing the buttons do not say. Which values count as large
                decides the call, and there is nothing else on this screen that
                names them. */}
            <p className="text-ink-muted">
              Large numbers come from {NUMBERS_LARGE_TILES.slice(0, -1).join(', ')} and{' '}
              {NUMBERS_LARGE_TILES[NUMBERS_LARGE_TILES.length - 1]}. The rest come from 1 to 10.
            </p>
            {/* What happens if the caller does nothing, which the running clock
                does not say. It goes once there is a call, rather than being
                replaced by a longer line: the pressed button is already lit, so
                "you called two" was the same fact twice and "the tiles land
                next" was about to be self-evident. The slot stays reserved so
                the card does not close up under the tap. */}
            <p className={`${MONO_LABEL} min-h-[14px] leading-[14px]`} aria-live="polite">
              {chosen === null ? 'if the clock runs out the board is drawn for you' : ''}
            </p>
          </div>
        </section>
      ) : (
        <div className="flex items-center justify-center gap-2">
          {chooser !== null && <PlayerChip player={chooser} size="md" />}
          <span className="text-[15px] text-ink-muted">
            {chooser === null ? 'nobody is choosing' : 'is choosing'}
          </span>
        </div>
      )}
    </div>
  );
}
